import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/* One agency's analytics, for the admin dashboard's per-company Reports tab.
 *
 * These are the SAME endpoints the agency's own reporting screens call — the
 * API computes a company report once and both audiences read it. What differs
 * is who is asking: an agency is scoped to itself by the server, while platform
 * staff have no company of their own and therefore name one with ?companyId=.
 * adminCompanyScope (backend src/middleware/agentAccess.js) resolves that
 * parameter for admins and ignores it for everybody else, so nothing here can
 * widen an agency's own access.
 *
 * Base URL is /api/company-reports, NOT /api/company-plans-admin: the plans
 * admin surface manages the subscription, this one reports on the stock. Same
 * base query, same token key and the same tag-based invalidation as every other
 * service in this folder. */
export const companyReportApi = createApi({
    reducerPath: 'companyReportApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST}/api/company-reports`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['CompanyOverview', 'CompanyAgents'],
    endpoints: (build) => ({
        /* Everything the tab's charts read, in ONE request: KPIs, the status
           split, the property-type / listing-type / city breakdowns, the daily
           activity series, the promotion summary and the attention counters.
           `from` and `to` are optional YYYY-MM-DD; the server defaults to the
           last 30 days and echoes what it used in series_meta. */
        getCompanyOverview: build.query({
            query: ({ companyId, from, to }) => {
                const params = new URLSearchParams({ companyId: String(companyId) });
                if (from) params.set('from', from);
                if (to) params.set('to', to);
                return `/overview?${params.toString()}`;
            },
            providesTags: (result, error, arg) => [
                { type: 'CompanyOverview', id: arg?.companyId },
            ],
        }),

        /* The per-agent leaderboard. Served on its own because it paginates and
           because an agency with no agents should not make the overview fail —
           the tab renders the charts either way. */
        getCompanyAgents: build.query({
            query: ({ companyId, page = 1, limit = 25 }) => {
                const params = new URLSearchParams({
                    companyId: String(companyId),
                    page: String(page),
                    limit: String(limit),
                });
                return `/agents?${params.toString()}`;
            },
            providesTags: (result, error, arg) => [
                { type: 'CompanyAgents', id: arg?.companyId },
            ],
        }),
    }),
});

export const { useGetCompanyOverviewQuery, useGetCompanyAgentsQuery } = companyReportApi;
